import {Button, ControlledDatepicker, GenericInput} from '@hm/fundamental'
import {mdiClose, mdiMagnify, mdiRefresh} from '@mdi/js'
import {
  BaseFilter,
  CollapsibleFilterWrapper,
  FiltersCollapseProvider,
  FiltersHeader,
  UpmHierarchyFilters
} from '@widgets/report-filters'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {defaultFiltersFormValues, useFiltersContext} from '../../providers/FiltersProvider'

import {buildReportPayload} from '@refra/modules/refra-flexible-report/helpers'
import {
  AllowedAssetConfigDataNames,
  AllowedRefractoryProductDataNames,
  FiltersFormValues,
  UpmFilterValue
} from '@refra/modules/refra-flexible-report/types'

const UPM_HIERARCHY_LEVELS = ['ManagementArea', 'Country', 'Plant'] as const

export const Filters = () => {
  const {t} = useTranslation()
  const {control, reset, getValues, setValue} = useFormContext<FiltersFormValues>()
  const {
    upmFilterValues,
    assetConfigFilterValues,
    refractoryManufacturerFilterValues,
    commitManufacturerFilters,
    refractoryProductFilterValues,
    isFetchingProducts,
    kilnHeatZoneFilterValues,
    syncUpmFilters,
    filteredKilns,
    isFetching,
    generateReport
  } = useFiltersContext()

  const upmFilters = useWatch({control, name: 'upmFilters'})
  const refractoryManufacturerFilters = useWatch({control, name: 'refractoryManufacturerFilters'})

  const upmHierarchyValues: Record<'ManagementArea' | 'Country' | 'Plant', UpmFilterValue[]> = {
    ManagementArea: upmFilterValues.ManagementArea as UpmFilterValue[],
    Country: upmFilterValues.Country as UpmFilterValue[],
    Plant: upmFilterValues.Plant as UpmFilterValue[]
  }

  const handleClearFilters = () => {
    reset(defaultFiltersFormValues)
    commitManufacturerFilters([])
  }

  const handleGenerateReport = async () => {
    await generateReport(buildReportPayload(getValues(), filteredKilns))
  }

  const renderAssetConfigFilter = (dataName: AllowedAssetConfigDataNames) => {
    const options = assetConfigFilterValues[dataName].map((option) => ({
      label: t(`refraFlexibleReport.filters.assetConfig.${dataName}.options.${option}`),
      value: option
    }))
    return (
      <Controller
        control={control}
        name={`assetConfigFilters.${dataName}`}
        render={({field}) => (
          <BaseFilter
            name={`asset_config_${dataName}`}
            title={t(`refraFlexibleReport.filters.assetConfig.${dataName}.title`)}
            options={options}
            selectedValues={field.value ?? []}
            setSelectedValues={field.onChange}
            isCollapsible={false}
            useAutocomplete={true}
          />
        )}
      />
    )
  }

  const renderRefractoryProductFilter = (dataName: AllowedRefractoryProductDataNames) => {
    const disabled = dataName === 'product' && refractoryManufacturerFilters.length === 0
    return (
      <div className={disabled ? 'opacity-50 pointer-events-none' : ''}>
        <Controller
          control={control}
          name={`refractoryProductFilters.${dataName}`}
          render={({field}) => (
            <BaseFilter
              name={`refractory_${dataName}`}
              title={t(`refraFlexibleReport.filters.productCatalog.${dataName}`)}
              options={refractoryProductFilterValues[dataName]}
              selectedValues={field.value ?? []}
              setSelectedValues={field.onChange}
              isCollapsible={true}
              useAutocomplete={dataName === 'classification'}
              isLoading={dataName === 'product' && isFetchingProducts}
            />
          )}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <FiltersCollapseProvider>
        <FiltersHeader title={t('refraFlexibleReport.filters.title')} />
        <div className="flex flex-col gap-2">
          <Controller
            control={control}
            name="searchFilter"
            render={({field}) => (
              <GenericInput
                name="refra_kiln_search_filter"
                value={field.value}
                icon={mdiMagnify}
                placeholder={'Search...'}
                onChange={field.onChange}
                data-cy="refra_kiln_search_filter"
              />
            )}
          />

          <UpmHierarchyFilters
            levels={UPM_HIERARCHY_LEVELS}
            upmFilterValues={upmHierarchyValues}
            selectedByLevel={upmFilters}
            onSyncLevel={syncUpmFilters}
            getLevelLabel={(level) => t(`refraFlexibleReport.filters.upm.${level}`)}
          />

          <CollapsibleFilterWrapper title={'Process'}>
            <div className="flex gap-2 flex-col">
              {renderAssetConfigFilter('separator')}
              {renderAssetConfigFilter('preheater')}
              {renderAssetConfigFilter('calciner')}
            </div>
          </CollapsibleFilterWrapper>

          {renderRefractoryProductFilter('classification')}

          <Controller
            control={control}
            name="refractoryManufacturerFilters"
            render={({field}) => (
              <BaseFilter
                name="refractory_manufacturer"
                title={t('refraFlexibleReport.filters.manufacturer.title')}
                options={refractoryManufacturerFilterValues}
                selectedValues={field.value ?? []}
                setSelectedValues={(values) => {
                  field.onChange(values)
                  if (values.length === 0) {
                    setValue('refractoryProductFilters.product', [])
                    commitManufacturerFilters([])
                  }
                }}
                onBlur={() => commitManufacturerFilters(field.value ?? [])}
                useAutocomplete={true}
                displayTitle={true}
              />
            )}
          />

          {renderRefractoryProductFilter('product')}

          <Controller
            control={control}
            name="kilnHeatZoneFilters"
            render={({field}) => (
              <BaseFilter
                name="kiln_heat_zone"
                title={t('refraFlexibleReport.filters.heatZones.title')}
                options={kilnHeatZoneFilterValues.map((zone) => ({
                  label: t(`refraFlexibleReport.filters.heatZones.options.${zone.enumValue}`),
                  value: zone.enumValue
                }))}
                selectedValues={field.value}
                setSelectedValues={field.onChange}
              />
            )}
          />

          <CollapsibleFilterWrapper title={t('refraFlexibleReport.filters.feedingDate.title')}>
            <div className="grid grid-cols-2 gap-2 min-w-0">
              <Controller
                control={control}
                name="feedingDateFilter.from"
                render={({field}) => (
                  <ControlledDatepicker
                    name="feeding_date_from"
                    label={t('refraFlexibleReport.filters.feedingDate.from')}
                    value={field.value}
                    onChange={field.onChange}
                    data-cy="refra_feeding_date_from"
                  />
                )}
              />
              <Controller
                control={control}
                name="feedingDateFilter.to"
                render={({field}) => (
                  <ControlledDatepicker
                    name="feeding_date_to"
                    label={t('refraFlexibleReport.filters.feedingDate.to')}
                    value={field.value}
                    onChange={field.onChange}
                    data-cy="refra_feeding_date_to"
                    showValidationErrorOnTouched={false}
                  />
                )}
              />
            </div>
          </CollapsibleFilterWrapper>
        </div>
      </FiltersCollapseProvider>

      <div className="sticky bottom-0 mt-auto flex justify-between border-t border-gray-200 bg-white pt-4">
        <Button
          variant="tertiary"
          size="large"
          icon={mdiClose}
          iconPosition="left"
          onClick={handleClearFilters}
          data-cy="refra_clear_filters_btn"
        >
          {t('refraFlexibleReport.filters.actions.clearFilters')}
        </Button>
        <Button
          variant="primary"
          size="large"
          icon={mdiRefresh}
          className={isFetching ? '[&_svg]:animate-spin' : undefined}
          onClick={handleGenerateReport}
          disabled={isFetching}
          data-cy="refra_generate_report_btn"
        >
          {t('refraFlexibleReport.filters.actions.generateReport')}
        </Button>
      </div>
    </div>
  )
}
